import { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { Draggable } from '@fullcalendar/interaction';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import Table from '../common/table/Table.jsx';
import { CalendarView } from '../calendar/CalendarView.jsx';
import CalendarFilters from '../calendar/CalendarFilters.jsx';
import useAgendaData from '../../hooks/calendar/useAgendaData.js';
import resolveAgendaView from '../../helpers/calendar/resolveAgendaView.js';
import splitAgendaPanes, { CALENDAR_SHARE } from '../../helpers/calendar/splitAgendaPanes.js';
import shareFromDrag, { MIN_SHARE, MAX_SHARE, STEP, clampShare } from '../../helpers/calendar/shareFromDrag.js';
import useMaximised from '../../hooks/useMaximised.js';
import { taskInLocation } from '../../helpers/locations/locationFilter'

/** Waar de browser de gekozen verdeling bewaart; per persoon, niet per organisatie. */
const SHARE_KEY = 'householdAgenda.calendarShare';

/**
 * De bewaarde verdeling, of de standaard. Een onbruikbare waarde (met de hand
 * aangepast, van een oudere versie) telt als 'niets bewaard'. localStorage kan
 * ontbreken of weigeren (privévenster, geblokkeerde opslag); dan ook de
 * standaard — dit is een gemak, geen gegeven dat verloren mag gaan.
 * @returns {number}
 */
const readShare = () => {
    try {
        const bewaard = Number.parseFloat(window.localStorage.getItem(SHARE_KEY));
        if (Number.isFinite(bewaard) && bewaard >= MIN_SHARE && bewaard <= MAX_SHARE) return bewaard;
    } catch (e) {
        // Geen opslag beschikbaar: zie hierboven.
    }
    return CALENDAR_SHARE;
};

/** @param {number} share */
const saveShare = (share) => {
    try {
        window.localStorage.setItem(SHARE_KEY, String(share));
    } catch (e) {
        // Geen opslag beschikbaar: de keuze geldt dan alleen voor nu.
    }
};

/**
 * De lijst met huishoudens naast de agenda, met slepen van links naar rechts.
 *
 * WAAROM ÉÉN SCHERM: inplannen was twee schermen — in Huishoudens opzoeken wie
 * er aan de beurt is, onthouden welk adres dat was, naar de Agenda, en daar het
 * bezoek aanmaken. Onder elkaar hoeft dat onthouden niet meer.
 *
 * De agenda staat BOVEN en de lijst eronder: een weekweergave heeft de volle
 * breedte nodig om leesbaar te zijn. Allebei passen ze samen op één scherm —
 * dat is geen smaak maar een voorwaarde, want je moet het doel van je sleep
 * kunnen zien terwijl je begint.
 *
 * Het slepen maakt NIETS aan: het opent de taakbewerker met een voorgevuld
 * huisbezoek op het tijdslot waar je losliet. De duur en de medewerker staan
 * dan nog nergens, en een halve afspraak die er al wél staat is erger dan geen.
 * AI-CONTEXT: See docs/pitfalls.md#state-nav-step-advances-without-saving
 *
 * ALLEEN OP VOLLEDIG SCHERM (verzoek BuurtKlusBedrijf): wie dit opent komt om
 * te plannen, niet om de pagina eromheen te zien. Een versie tussen de
 * paginakop en het menu is er niet meer; 'Verkleinen' (of Esc) is daarom
 * hetzelfde als sluiten, en dat meldt dit component via `onClose` — de pagina
 * wisselt dan terug naar de tabel.
 *
 * @param {object} props
 * @param {Array<object>} props.items - De huishoudens die in de tabel horen, al gefilterd op status
 * @param {boolean} props.loaded - Of de huishoudens binnen zijn; false = nog onderweg, niet 'leeg'
 * @param {Array<object>} props.columns - Kolomdefinities, dezelfde als het tabeloverzicht
 * @param {(item: object) => React.ReactNode} props.renderRow - Rendert de cellen van één rij
 * @param {(item: object) => string} props.getLabel - Het adres van een huishouden, voor de sleepschaduw
 * @param {boolean} props.canEdit - Of deze gebruiker bezoeken mag inplannen
 * @param {string} [props.userId] - Auth-id, voor een nieuwe taak
 * @param {string} [props.defaultAgendaView] - De ingestelde standaardweergave van de agenda
 * @param {() => void} [props.onClose] - Wordt aangeroepen bij 'Verkleinen' of Esc; de pagina hoort dan terug te gaan naar de tabel
 * @param {string} [props.selectedLocationId] - De locatiekeuze van het scherm; leeg = alle
 * @param {Record<string, string>} [props.locationByAdressId] - Huishouden-ID -> locatie
 * @param {Record<string, import('../../helpers/calendar/adressStatusById').AdressStatusBadge>} [props.adressStatusById] - Status (woord + kleur) per huishouden, voor het bolletje in het agendablok
 * @returns {JSX.Element}
 */
const HouseholdAgendaView = ({
    onClose,
    items,
    loaded,
    columns,
    renderRow,
    getLabel,
    canEdit,
    userId,
    defaultAgendaView,
    selectedLocationId = '',
    locationByAdressId,
    adressStatusById,
}) => {

    const {
        visibleDocs,
        personas,
        setDateRange,
        refresh,
        personaAbsences,
        roleColorByPersonaId,
        personaColors,
        scheduleEndByPersonaId,
    } = useAgendaData();

    // De schil van de pagina (menu, topbalk, paginakop) gaat via een klasse op
    // body weg; verlaten meldt de hook aan de pagina.
    const { exit: close } = useMaximised({ initial: true, onExit: onClose });

    // Hoeveel van de verdeelbare hoogte de agenda krijgt; de planner verschuift
    // het met de scheidslijn en de browser onthoudt het.
    const [share, setShare] = useState(readShare);

    /** @type {React.RefObject<HTMLDivElement>} */
    const wrapRef = useRef(null);
    /** @type {React.RefObject<HTMLDivElement>} */
    const listBlockRef = useRef(null);
    /** @type {React.RefObject<HTMLDivElement>} */
    const calendarBlockRef = useRef(null);

    // Allebei de hoogten in één keer: de agenda en het schuifvak van de tabel
    // verdelen dezelfde ruimte. De tabel uit de agenda afleiden (of andersom) is
    // rond, en dat kostte precies de fout die dit oplost — het geheel stak 100px
    // onder het scherm uit en er is geen schuifbalk die dat terughaalt.
    const [panes, setPanes] = useState(() => splitAgendaPanes({}));

    // Gemeten en niet geschat: boven deze weergave staat een kop, een
    // beschrijving en de statusknoppen, en hoeveel dat is verschilt per
    // organisatie en per melding die erboven hangt.
    const meet = useCallback(() => {
        const top = wrapRef.current?.getBoundingClientRect().top;

        // Wat er in elk blok BOVEN het schuifbare deel staat, afgeleid uit het
        // verschil tussen het blok en zijn inhoud. Afgeleid en niet als getal
        // opgeschreven: een vast getal hier is een tweede waarheid naast de CSS.
        // AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
        const agendaBlok = calendarBlockRef.current;
        const kalender = agendaBlok?.querySelector('.fc');
        const lijstBlok = listBlockRef.current;
        const schuifvak = lijstBlok?.querySelector('.common-table__container');

        setPanes(splitAgendaPanes({
            viewportHeight: window.innerHeight,
            topOffset: typeof top === 'number' ? Math.max(0, Math.round(top)) : 0,
            calendarChrome: agendaBlok && kalender
                ? Math.max(0, agendaBlok.offsetHeight - kalender.offsetHeight)
                : 0,
            listChrome: lijstBlok && schuifvak
                ? Math.max(0, lijstBlok.offsetHeight - schuifvak.offsetHeight)
                : 0,
            calendarShare: share,
        }));
    }, [share]);

    // Het slepen van de scheidslijn. In refs en niet in state: tijdens een sleep
    // komen er tientallen bewegingen per seconde binnen, en de startstand mag
    // daar niet bij veranderen.
    /** @type {React.MutableRefObject<{startY: number, startShare: number, total: number} | null>} */
    const dragRef = useRef(null);

    const dragMove = useCallback((e) => {
        const sleep = dragRef.current;
        if (!sleep) return;
        setShare(shareFromDrag({ startShare: sleep.startShare, deltaY: e.clientY - sleep.startY, total: sleep.total }));
    }, []);

    const dragEnd = useCallback(() => {
        if (!dragRef.current) return;
        dragRef.current = null;
        window.removeEventListener('pointermove', dragMove);
        window.removeEventListener('pointerup', dragEnd);
        setShare((huidig) => { saveShare(huidig); return huidig; });
    }, [dragMove]);

    const dragStart = (e) => {
        e.preventDefault();
        dragRef.current = {
            startY: e.clientY,
            startShare: share,
            // Agenda en tabel samen: precies wat splitAgendaPanes verdeelt.
            total: panes.calendarHeight + panes.listMaxHeight,
        };
        window.addEventListener('pointermove', dragMove);
        window.addEventListener('pointerup', dragEnd);
    };

    useEffect(() => () => {
        window.removeEventListener('pointermove', dragMove);
        window.removeEventListener('pointerup', dragEnd);
    }, [dragMove, dragEnd]);

    // Pijltjes omhoog/omlaag: dezelfde scheidslijn zonder muis.
    const dragKey = (e) => {
        const richting = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
        if (richting === 0) return;
        e.preventDefault();
        const nieuw = clampShare(share + richting * STEP);
        setShare(nieuw);
        saveShare(nieuw);
    };

    // useLayoutEffect: meten vóór de browser tekent, anders zie je de agenda
    // één beeld lang op de verkeerde hoogte springen.
    useLayoutEffect(() => { meet(); }, [meet, items.length, loaded]);

    useEffect(() => {
        window.addEventListener('resize', meet);
        return () => window.removeEventListener('resize', meet);
    }, [meet]);

    // Een roosterregel draagt zijn locatie zelf, een huisbezoek erft hem van het
    // huishouden -- dezelfde regel als op de pagina Agenda.
    const docsOpLocatie = useMemo(
        () => (Array.isArray(visibleDocs) ? visibleDocs : [])
            .filter((task) => taskInLocation(task, selectedLocationId, locationByAdressId)),
        [visibleDocs, selectedLocationId, locationByAdressId],
    );

    // De maat gaat als CSS-variabele naar het blok: de opmaak blijft daarmee in
    // het stylesheet staan, en er komt geen style-attribuut in de JSX. Een
    // maximum op de wikkel is er niet meer: die vult het venster en schuift
    // niet; het schuifvak van de tabel vangt wat er niet past.
    useLayoutEffect(() => {
        listBlockRef.current?.style.setProperty('--household-agenda-list-max', `${panes.listMaxHeight}px`);
    }, [panes]);

    /** @type {React.MutableRefObject<{destroy: () => void} | null>} */
    const draggableRef = useRef(null);

    /**
     * Meldt het blok om de tabel bij FullCalendar aan als sleepbron.
     *
     * Een CALLBACK-ref en geen effect: React roept deze functie aan zodra het
     * blok er is, en met `null` als het verdwijnt. Een effect dat alleen bij het
     * opstarten draait vond niets om aan te melden zolang de huishoudens nog
     * onderweg waren, en probeerde het daarna nooit meer — je kon dan geen
     * huishouden oppakken, zonder fout en met een greep die er gewoon stond.
     *
     * `create: false` voorkomt dat er meteen een blok in de agenda verschijnt:
     * zonder die vlag maakt FullCalendar van de gesleepte rij een echte afspraak
     * met de tekst van de rij als titel, en die staat er dan al voordat de
     * gebruiker iets bevestigd heeft.
     *
     * @param {HTMLDivElement | null} node - Het blok om de tabel, of null bij verdwijnen
     * @returns {void}
     */
    const rowsRef = useCallback((node) => {
        if (draggableRef.current) {
            draggableRef.current.destroy();
            draggableRef.current = null;
        }
        if (!node || !canEdit) return;

        draggableRef.current = new Draggable(node, {
            itemSelector: 'tr.household-agenda__row',
            eventData: () => ({ create: false }),
        });
    }, [canEdit]);

    return (
        <div className="household-agenda" ref={wrapRef}>
            {/* Een kruisje in de hoek en geen eigen balk: elke pixel boven de
                agenda gaat van het werkvlak af (verzoek BuurtKlusBedrijf). Een
                echte knop met een naam; Esc doet hetzelfde. */}
            <button
                type="button"
                className="household-agenda__close"
                onClick={close}
                aria-label="Sluiten"
                title="Terug naar de tabel (Esc)"
            >
                <CloseOutlinedIcon fontSize="small" />
            </button>
            <div className="household-agenda__calendar" ref={calendarBlockRef}>
                {/* Het locatiefilter van dit scherm geldt ook voor de agenda
                    erboven. Zonder dit is de lijst gefilterd en de agenda niet:
                    één scherm dat twee dingen beweert. Geen eigen keuzelijst in
                    de filterzijbalk, want die staat hier al boven de lijst.
                    AI-CONTEXT: See docs/features/huishouden-locaties.md */}
                <CalendarFilters docs={docsOpLocatie} currentUserId={userId}>
                    {({ filteredDocs, setSelectedTypes, selectedTypes, selectedPersonaIds }) => (
                        <CalendarView
                            docs={filteredDocs}
                            // Met precies één medewerker kan het rooster in week en dag een achtergrond zijn.
                            selectedPersonaIds={selectedPersonaIds}
                            scheduleDocs={visibleDocs}
                            userId={userId}
                            initialview={resolveAgendaView(defaultAgendaView)}
                            onDateRangeChange={setDateRange}
                            onStaleData={refresh}
                            onApplyTypeFilter={setSelectedTypes}
                            selectedTypes={selectedTypes}
                            roleColorByPersonaId={roleColorByPersonaId}
                            personaColorById={personaColors}
                            scheduleEndByPersonaId={scheduleEndByPersonaId}
                            personaAbsences={personaAbsences}
                            adressStatusById={adressStatusById}
                            // Zet de roosterweergave aan: dezelfde knop als op de
                            // pagina Agenda. Zonder deze prop valt die knop stil
                            // weg -- hij hangt eraan, niet aan de weergave zelf.
                            timelinePersonas={personas}
                            showSearch
                            acceptAdressDrop
                            height={panes.calendarHeight}
                        />
                    )}
                </CalendarFilters>
            </div>
            {/* De scheidslijn: slepen met de muis, of met de pijltjes als hij
                de focus heeft. `aria-valuenow` is het aandeel van de agenda in
                procenten. Zichtbaar als greepje, niet alleen bij hover.
                AI-CONTEXT: See docs/pitfalls.md#ui-hover-only-affordance-dies-on-mobile */}
            <div
                className="household-agenda__divider"
                role="separator"
                aria-orientation="horizontal"
                aria-label="Verdeling tussen agenda en tabel"
                aria-valuemin={Math.round(MIN_SHARE * 100)}
                aria-valuemax={Math.round(MAX_SHARE * 100)}
                aria-valuenow={Math.round(share * 100)}
                tabIndex={0}
                onPointerDown={dragStart}
                onKeyDown={dragKey}
            >
                <span className="household-agenda__divider-grip" aria-hidden="true" />
            </div>
            <div className="household-agenda__list" ref={listBlockRef}>
                <h3 className="household-agenda__list-heading">
                    Huishoudens
                    {loaded && <span className="household-agenda__count">{items.length}</span>}
                </h3>
                {/* De wikkel staat er ALTIJD, ook tijdens het laden: hieraan hangt
                    de sleepbron. Hing die aan de rijen zelf, dan bestaat er bij
                    het openen niets om aan te melden en probeert React het daarna
                    nooit meer — je kon dan geen huishouden oppakken. */}
                <div className="household-agenda__rows" ref={rowsRef}>
                    {/* Twee takken op volgorde van zekerheid: eerst 'weet ik nog
                        niet', dan pas de tabel met zijn eigen lege tekst. 'Geen
                        huishoudens' is een bewering aan de lezer, en die is
                        onwaar zolang de gegevens onderweg zijn.
                        AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */}
                    {!loaded ? (
                        <p className="household-agenda__empty">Huishoudens worden opgehaald…</p>
                    ) : (
                        <Table
                            columns={columns}
                            items={items}
                            renderRow={renderRow}
                            getItemKey={(item) => item.ID}
                            /* Zoeken staat uit: het zoekveld van het overzicht
                               doorzoekt ÁLLE gegevens, en die lijst wordt alleen
                               opgehaald in de tabelweergave. Hier zou hij dus
                               stil niets vinden. Filteren doe je met de
                               statusknoppen boven de agenda. */
                            showSearch={false}
                            /* Rijen herordenen doet hier niets, en zou vechten
                               met het slepen naar de agenda. */
                            draggable={false}
                            containerClassName="household-agenda__table"
                            getRowClassName={() => 'household-agenda__row'}
                            rowProps={(item) => ({
                                'data-adressid': item.ID,
                                'data-adresslabel': getLabel(item),
                            })}
                            emptyMessage="Geen huishoudens in deze status."
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default HouseholdAgendaView;
